'use client';

import { useSelector } from '@xstate/store/react';
import { useTranslations } from 'next-intl';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { useEffect, useMemo, useRef, useState, useTransition } from 'react';

import { GTMItemListName, sendGTMEvent } from '@/fo/api/gtm';
import { GraphQLResponse, useGraphQLDebug } from '@/fo/api/magento';
import { MindboxPageView, mindboxViewCategory } from '@/fo/api/mindbox';
import { FeatureProductTileBase } from '@/fo/feature/product/tile/base';
import { UiKitBannerHero } from '@/fo/ui/kit/banner/hero';
import {
  DEFAULT_SORT_OPTION,
  FilterChangeArgs,
  FilterKeys,
  FilterOption,
  SelectedFilters,
  TopCategories,
  UiProductLayoutCatalog
} from '@/fo/ui/product/layout/catalog';
import { MappedProduct } from '@/fo/util/map-product-data';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import {
  storeFilters,
  selectFilters
} from '../../../../../../store/filters/src/lib/store';
import { GetCategoriesResponse } from '../api/getCategories';
import { getProducts, GetProductsResponse } from '../api/getProducts';
import { getProductsCount } from '../api/getProductsCount';
import { PAGE_SIZE } from '../constants';
import { DefaultCategoryPath, PaginationParams } from '../types';
import { formatCategoryParams, updateFilters } from '../util';
import { parseAsObject } from '../util/parseAsObject';

import { styles } from './FeatureProductCatalogBase.styles';

/** Скроллить к каталогу только если пользователь проскроллил больше двух строк товаров */
function shouldScrollToCatalogTop(listElement: HTMLDivElement | null): boolean {
  if (!listElement || typeof window === 'undefined') return false;

  const listTop = listElement.getBoundingClientRect().top + window.scrollY;
  const scrolledPastList = window.scrollY - listTop;

  if (scrolledPastList <= 0) return false;

  const productGrid = listElement.querySelector('.grid');
  const gridChildren = productGrid?.querySelectorAll(':scope > *');

  let twoRowsHeight: number;
  if (gridChildren && gridChildren.length >= 5) {
    const row1Top = (gridChildren[0] as HTMLElement).getBoundingClientRect()
      .top;
    const row3Start = (gridChildren[4] as HTMLElement).getBoundingClientRect()
      .top;
    twoRowsHeight = row3Start - row1Top;
  } else {
    twoRowsHeight = 400;
  }

  return scrolledPastList > twoRowsHeight;
}

export interface MediaBannerHero {
  id: number;
  alt: string;
  updatedAt: string;
  createdAt: string;
  url?: string | null;
  thumbnailURL?: string | null;
  filename?: string | null;
  mimeType?: string | null;
  filesize?: number | null;
  width?: number | null;
  height?: number | null;
  focalX?: number | null;
  focalY?: number | null;
  sizes?: {
    desktop?: {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
      filesize?: number | null;
      filename?: string | null;
    };
    'desktop@2x'?: {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
      filesize?: number | null;
      filename?: string | null;
    };
    tablet?: {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
      filesize?: number | null;
      filename?: string | null;
    };
    'tablet@2x'?: {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
      filesize?: number | null;
      filename?: string | null;
    };
    mobile?: {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
      filesize?: number | null;
      filename?: string | null;
    };
    'mobile@2x'?: {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
      filesize?: number | null;
      filename?: string | null;
    };
    mobileSmall?: {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
      filesize?: number | null;
      filename?: string | null;
    };
    'mobileSmall@2x'?: {
      url?: string | null;
      width?: number | null;
      height?: number | null;
      mimeType?: string | null;
      filesize?: number | null;
      filename?: string | null;
    };
  };
}
export interface BannerCategory {
  mode?: ('light' | 'dark') | null;
  image: number | MediaBannerHero;
  title: string;
  subtitle?: string | null;
  alignText?: ('centerLeft' | 'bottomLeft' | 'center') | null;
  buttons?:
    | {
        buttonLabel?: string | null;
        buttonHref?: string | null;
        buttonTarget?: ('_blank' | '_self') | null;
        id?: string | null;
      }[]
    | null;
  id?: string | null;
  blockName?: string | null;
  blockType: 'bannerCategory';
}
export interface FeatureProductCatalogBaseProps {
  isAuthorized: boolean;
  loyaltyLevel?: number;
  data: Partial<GetCategoriesResponse> &
    GetProductsResponse & {
      defaultCategoryPath?: DefaultCategoryPath;
      selectedFilters?: SelectedFilters;
      selectedSort?: string;
      pagination?: { page: number };
      query?: string;
    };
  topSeoBlock?: React.ReactNode;
  bottomSeoBlock?: React.ReactNode;
  hiddenFiltersSlug?: string[];
  hideCareOptions?: boolean;
  listName: GTMItemListName;
  topCategories?: TopCategories[];
  listBanners?: BannerCategory[] | null;
}

export function FeatureProductCatalogBase({
  isAuthorized,
  data: initialData,
  topSeoBlock,
  bottomSeoBlock,
  hiddenFiltersSlug,
  listName,
  topCategories,
  listBanners
}: FeatureProductCatalogBaseProps) {
  const filters = useSelector(storeFilters, selectFilters);
  const slots = styles();

  const selectedFilters = useRef<SelectedFilters>({});
  const listRef = useRef<HTMLDivElement>(null);
  const [tempFilters, setTempFilters] = useState<SelectedFilters>(
    initialData?.selectedFilters || {}
  );
  const [selectedSort, setSelectedSort] = useState<string>(
    initialData?.selectedSort || DEFAULT_SORT_OPTION.value
  );
  const [products, setProducts] = useState<MappedProduct[]>(
    initialData.products || []
  );

  /** Ответ для debug логирования */
  const [debugResponse, setDebugResponse] = useState<
    GraphQLResponse<GetProductsResponse> | undefined
  >(
    initialData.debugInfo
      ? {
          data: initialData,
          errors: initialData.error,
          statusCode: 200,
          debugInfo: initialData.debugInfo
        }
      : undefined
  );

  // Log GraphQL debug info if available
  useGraphQLDebug(debugResponse, 'getProducts');

  useEffect(() => {
    storeFilters.send({
      type: 'setFilters',
      filters: initialData.filters,
      filterCategory: initialData.category?.id ?? 0
    });
  }, [initialData.filters, initialData.category?.id]);

  /** Синхронизируем ref применённых фильтров с данными с сервера (URL), чтобы при закрытии модалки не сбрасывать tempFilters */
  useEffect(() => {
    if (initialData?.selectedFilters != null) {
      selectedFilters.current = initialData.selectedFilters;
    }
  }, [initialData?.selectedFilters]);

  const [isLoading, startTransition] = useTransition();
  const [pagination, setPagination] = useState<PaginationParams>({
    count: initialData.page_info?.total_pages || 0,
    page:
      initialData?.pagination?.page || initialData.page_info?.current_page || 1
  });
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const t = useTranslations();
  const [productsCount, setProductsCount] = useState<number>(
    initialData?.total_count || 0
  );
  const gtmEventCategory = useMemo(() => {
    const category = initialData?.category as {
      name?: string;
      breadcrumbs?: { category_name?: string | null }[] | null;
    } | null;

    if (!category) return '';

    const mapName = (name: string) =>
      name === 'Default Category' ? t('catalog') : name;

    const breadcrumbLabels = [
      ...(category.breadcrumbs || [])
        .filter(Boolean)
        .map((breadcrumb) => mapName(String(breadcrumb?.category_name || ''))),
      mapName(category.name || '')
    ].filter(Boolean);

    return breadcrumbLabels.join('::') || t('catalog');
  }, [initialData?.category, t]);

  const mappedTopCategories =
    topCategories && topCategories.length > 0
      ? topCategories.map((item) => {
          if (item.url_key?.includes('catalog')) {
            return item;
          }

          return {
            ...item,
            url_key: `catalog/${item.url_key}`
          };
        })
      : [];

  const [, setSearchQuery] = useQueryStates(
    {
      filters: parseAsObject.withDefault(initialData?.selectedFilters || {}),
      sort: parseAsString.withDefault(
        initialData?.selectedSort || DEFAULT_SORT_OPTION.value
      ),
      page: parseAsInteger.withDefault(1)
    },
    {
      shallow: false,
      urlKeys: {
        filters: 'f',
        sort: 's',
        page: 'p'
      }
    }
  );

  const PRODUCT_COUNT = 8;
  const productChunkSize = Math.ceil(Number(products.length / PRODUCT_COUNT));
  const productsPerPage = initialData.page_info?.page_size ?? 36;
  const currentPage = initialData.page_info?.current_page ?? 1;
  const bannersPerPage = Math.floor(productsPerPage / PRODUCT_COUNT);

  function groupElements<T>(
    array: T[],
    chunkSize: number,
    countInChunk: number
  ) {
    const arrayCopy = array.slice();
    const groupedItems = [];

    if (chunkSize > 1) {
      for (let i = 0; i < chunkSize; i++) {
        const chunk = arrayCopy.splice(0, countInChunk);

        groupedItems.push(chunk);
      }
    } else {
      groupedItems.push(arrayCopy);
    }

    return groupedItems;
  }

  const mappedProducts = groupElements(
    products,
    productChunkSize,
    PRODUCT_COUNT
  );

  const mappedBanners =
    currentPage === 1
      ? listBanners
      : listBanners?.slice((currentPage - 1) * bannersPerPage);

  const updateQueryState = (
    partial: Partial<{
      filters: SelectedFilters | null;
      sort: string;
      page: number | null;
    }>
  ) => {
    if (partial.filters && Object.keys(partial.filters).length === 0) {
      partial.filters = null;
    }
    if (typeof partial.page === 'number' && partial.page <= 1) {
      partial.page = null;
    }
    setSearchQuery(partial, { history: 'push' });
  };

  const mainFilterSlugs = useMemo(() => {
    if (!filters.mainFilters) return [];
    return filters.mainFilters.map((option) => option && option.slug);
  }, [filters]);

  const getProductsParams = ({
    filtersParams,
    paginationParams,
    sortValueParams
  }: {
    filtersParams?: SelectedFilters;
    paginationParams?: PaginationParams;
    sortValueParams?: string;
  }) => {
    const newSelectedFilters = filtersParams || tempFilters;
    return formatCategoryParams({
      filtersParams: newSelectedFilters,
      paginationParams: paginationParams || pagination,
      sortValue: sortValueParams || selectedSort,
      defaultCategoryPath: initialData.defaultCategoryPath,
      category: initialData?.category,
      search: initialData?.query
    });
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setIsOpenModal(true);
      return;
    }
    setTempFilters(selectedFilters.current);
  };

  const onReset = (filterKey: FilterKeys) => {
    const newTempFilters = resetFiltersByKey(tempFilters, filterKey);
    setTempFilters(newTempFilters);
    updateQueryState({ filters: newTempFilters });
    onShowProducts(
      getProductsParams({ filtersParams: newTempFilters }),
      newTempFilters
    );
  };

  const resetFiltersByKey = (
    currentFilters: SelectedFilters,
    filterKey: FilterKeys
  ): SelectedFilters => {
    if (filterKey === FilterKeys.mainFilters && mainFilterSlugs.length === 0) {
      return {};
    }
    return Object.entries(currentFilters).reduce<SelectedFilters>(
      (acc, [key, value]) => {
        if (
          filterKey === FilterKeys.mainFilters &&
          mainFilterSlugs.includes(key)
        ) {
          return acc;
        } else {
          acc[key] = value;
          return acc;
        }
      },
      {}
    );
  };

  const fetchProductsCount = async (
    props: ReturnType<typeof getProductsParams>
  ) => {
    const count = await getProductsCount(props);
    setProductsCount(count);
  };

  const onFiltersChange = (args: FilterChangeArgs, withUpdate = false) => {
    const updatedFilters = updateFilters(tempFilters, args);
    setTempFilters(updatedFilters);

    if (withUpdate) {
      onShowProducts(
        getProductsParams({ filtersParams: updatedFilters }),
        updatedFilters
      );
      updateQueryState({ filters: updatedFilters });
    } else {
      fetchProductsCount(
        getProductsParams({
          filtersParams: updatedFilters
        })
      );
    }
  };

  const onShowProducts = async (
    props: ReturnType<typeof getProductsParams>,
    newSelectedFilters?: SelectedFilters
  ) => {
    startTransition(async () => {
      const response = await getProducts(props, hiddenFiltersSlug);

      // Update debug response for logging via useGraphQLDebug hook
      if (response.debugInfo) {
        setDebugResponse({
          data: response,
          errors: response.error,
          statusCode: 200,
          debugInfo: response.debugInfo
        });
      }

      storeFilters.send({
        type: 'updateFilters',
        filters: response.filters
      });
      setProducts(response.products);
      setProductsCount(response.total_count);
      setPagination({
        count: response.page_info?.total_pages || 0,
        page: response.page_info?.current_page || 1
      });
      setIsOpenModal(false);
      selectedFilters.current = newSelectedFilters || tempFilters;
      if (shouldScrollToCatalogTop(listRef.current)) {
        utilScrollToElement(listRef.current);
      }
    });
  };

  const handleSortChange = (option: FilterOption | null) => {
    const newSortValue = option?.value || '';
    const paginationParams = {
      count: pagination.count,
      page: 1
    };
    if (option?.label) {
      sendGTMEvent({
        event: 'sort_click',
        eventCategory: gtmEventCategory,
        eventLabel: option.label
      } as Parameters<typeof sendGTMEvent>[0]);
    }
    setSelectedSort(newSortValue);
    updateQueryState({ sort: newSortValue, page: paginationParams?.page });
    onShowProducts(
      getProductsParams({
        sortValueParams: newSortValue,
        paginationParams
      })
    );
  };

  const updatePagination = (
    paginationParams: Omit<PaginationParams, 'pageSize'>
  ) => {
    setPagination(paginationParams);
    onShowProducts(getProductsParams({ paginationParams }));
    updateQueryState({ page: paginationParams?.page || pagination.page });
  };

  const onLoadMore = () => {
    const nextPage = (pagination.page || 1) + 1;
    const paginationParams = {
      count: pagination.count,
      page: nextPage,
      pageSize: PAGE_SIZE
    };
    startTransition(async () => {
      const response = await getProducts(
        getProductsParams({ paginationParams })
      );

      // Update debug response for logging via useGraphQLDebug hook
      if (response.debugInfo) {
        setDebugResponse({
          data: response,
          errors: response.error,
          statusCode: 200,
          debugInfo: response.debugInfo
        });
      }

      updateQueryState({ page: paginationParams?.page || pagination.page });
      setProducts((prev) => [...prev, ...response.products]);
      storeFilters.send({
        type: 'updateFilters',
        filters: response.filters
      });
      setProductsCount(response.total_count);
      setPagination({
        count: response.page_info?.total_pages || 0,
        page: nextPage
      });
      selectedFilters.current = tempFilters;
    });
  };

  return (
    <>
      <MindboxPageView
        action={mindboxViewCategory}
        params={String(
          initialData?.defaultCategoryPath?.id ||
            initialData?.category?.id ||
            ''
        )}
      />

      <div ref={listRef}>
        <UiProductLayoutCatalog
          pagination={{
            count: pagination?.count || 0,
            page: pagination?.page || 0
          }}
          filters={filters}
          topCategories={mappedTopCategories}
          selectedFilters={tempFilters}
          selectedSort={selectedSort}
          onFiltersChange={onFiltersChange}
          onShowProducts={() => {
            onShowProducts(getProductsParams({}));
            updateQueryState({ filters: tempFilters });
          }}
          productsCount={productsCount}
          onOpenChange={onOpenChange}
          onReset={onReset}
          onSortChange={handleSortChange}
          setPagination={updatePagination}
          isLoading={isLoading}
          topSeoBlock={topSeoBlock}
          bottomSeoBlock={bottomSeoBlock}
          onLoadMore={onLoadMore}
          isOpenModal={isOpenModal}
        >
          {mappedProducts.map((products: MappedProduct[], index: number) => {
            return (
              <div
                className={slots.productBlock()}
                key={`${mappedProducts.length}-${index}`}
              >
                <div className={slots.productGrid()}>
                  {products.map((productData: MappedProduct, index: number) => (
                    <FeatureProductTileBase
                      key={`${productData.sku}-${index}`}
                      isAuthorized={isAuthorized}
                      data={productData}
                      listName={listName}
                      categoryName={initialData?.category?.name ?? ''}
                      positionIndex={index}
                    />
                  ))}
                </div>

                {Boolean(mappedProducts.length > 1) &&
                  Boolean(mappedBanners?.length) &&
                  mappedBanners?.[index] && (
                    <div className={'container'}>
                      <UiKitBannerHero
                        banners={[
                          {
                            mode: mappedBanners[index]?.mode || undefined,
                            image: {
                              sm:
                                typeof mappedBanners[index]?.image === 'object'
                                  ? mappedBanners[index]?.image.sizes?.mobile
                                      ?.url || ''
                                  : '',
                              md:
                                typeof mappedBanners[index]?.image === 'object'
                                  ? mappedBanners[index]?.image.sizes?.desktop
                                      ?.url || ''
                                  : ''
                            },
                            title: mappedBanners[index]?.title,
                            subtitle: mappedBanners[index]?.subtitle ?? '',
                            buttons: mappedBanners[index]?.buttons ?? [],
                            buttonText: '',
                            link: ''
                          }
                        ]}
                        type={'category'}
                      />
                    </div>
                  )}
              </div>
            );
          })}
        </UiProductLayoutCatalog>
      </div>
    </>
  );
}
